/**
 * girlcave overlay scrollbars — drives every [data-gc-scrollbar] rail.
 *
 * Figma 31:4460 (page) and 31:4484 (PDP info) show the rail only while the
 * user scrolls, and the PDP rail must end above the add-to-cart bar. A
 * styled native scrollbar can do neither (::-webkit-scrollbar opts Chrome
 * out of overlay scrollbars, always draws full-height, and cannot take
 * margins), so CSS hides the native bars and this file mirrors scroll
 * positions onto the overlay rails:
 *
 *   - show()   — .is-visible on; cleared 1s after the last scroll event.
 *   - update() — thumb size/offset from scrollHeight/clientHeight.
 *   - drag     — pointer capture on the thumb; a track press jumps, then
 *                continues as a drag.
 *
 * Real scrolling stays native (wheel, keyboard, touch); the rails are
 * purely visual, so they are presentational. Each rail declares its
 * scroller in data-gc-scrollbar (a CSS selector; empty/absent = the
 * document root, whose scroll event fires on window, not <html>).
 *
 * Soft page changes (assets/gc-navigation.js) swap the page without a load:
 * on `gc:page-load` rails that left the DOM are torn down (their window
 * listeners would otherwise keep the old page alive) and new ones — the PDP
 * info rail — are created.
 */

(function () {
  var IDLE_MS = 1000; // fade a rail this long after the last scroll
  var MIN_THUMB = 32; // px floor so very long pages stay grabbable

  var rails = []; // { bar, destroy }

  function createRail(bar) {
    if (rails.some(function (rail) { return rail.bar === bar; })) return;
    var selector = bar.getAttribute('data-gc-scrollbar');
    var scroller = selector ? document.querySelector(selector) : document.documentElement;
    if (!scroller) return;

    var isRoot = scroller === document.documentElement;
    // The root scroller fires its scroll event at the document (bubbling to
    // window); element scrollers fire on themselves.
    var scrollTarget = isRoot ? window : scroller;
    // Size-change proxy: the page watches <body>; a scoped rail watches its
    // own scrollport.
    var resizeTarget = isRoot ? document.body : scroller;

    var thumb = bar.firstElementChild;
    if (!thumb) return;

    var hideTimer = null;
    var frame = 0;
    var dragging = false;
    var dragStartY = 0;
    var dragStartScroll = 0;

    function maxScroll() {
      return scroller.scrollHeight - scroller.clientHeight;
    }

    function update() {
      var over = maxScroll();
      if (over <= 0) {
        // Nothing to scroll (short page, empty scrollport, or a
        // scroll-locked drawer on the page rail).
        bar.classList.remove('is-visible');
        clearTimeout(hideTimer);
        return;
      }
      var railH = bar.clientHeight;
      if (railH <= 0) return;
      var thumbH = Math.min(
        Math.max((scroller.clientHeight / scroller.scrollHeight) * railH, MIN_THUMB),
        railH
      );
      var progress = Math.min(Math.max(scroller.scrollTop / over, 0), 1);
      thumb.style.height = thumbH + 'px';
      thumb.style.transform = 'translateY(' + progress * (railH - thumbH) + 'px)';
    }

    function hide() {
      if (dragging) return;
      bar.classList.remove('is-visible');
    }

    function show() {
      update();
      if (maxScroll() <= 0) return;
      bar.classList.add('is-visible');
      clearTimeout(hideTimer);
      hideTimer = setTimeout(hide, IDLE_MS);
    }

    function scheduleUpdate() {
      if (frame) return;
      frame = requestAnimationFrame(function () {
        frame = 0;
        update();
      });
    }

    function startDrag(pointerId, clientY, captureEl) {
      dragging = true;
      dragStartY = clientY;
      dragStartScroll = scroller.scrollTop;
      captureEl.setPointerCapture(pointerId);
    }

    // Pointer capture is released implicitly on pointerup/pointercancel.
    function endDrag() {
      if (!dragging) return;
      dragging = false;
      show(); // fade from now, not from the last move
    }

    // Thumb grab.
    thumb.addEventListener('pointerdown', function (e) {
      if (maxScroll() <= 0) return;
      e.preventDefault();
      startDrag(e.pointerId, e.clientY, thumb);
    });

    // Track press: jump so the thumb centres under the pointer, then keep
    // dragging from the new position (press-and-hold scrolls further).
    bar.addEventListener('pointerdown', function (e) {
      if (e.target !== bar || maxScroll() <= 0) return;
      var rect = bar.getBoundingClientRect();
      var thumbH = thumb.getBoundingClientRect().height;
      var span = rect.height - thumbH;
      if (span <= 0) return;
      var progress = Math.min(Math.max((e.clientY - rect.top - thumbH / 2) / span, 0), 1);
      scroller.scrollTop = progress * maxScroll();
      update();
      e.preventDefault();
      startDrag(e.pointerId, e.clientY, bar);
    });

    // Moves land here in both drag modes: capture set on thumb bubbles up,
    // capture set on bar targets it directly.
    bar.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var railH = bar.clientHeight;
      if (railH <= 0) return;
      var next = dragStartScroll + (e.clientY - dragStartY) * (scroller.scrollHeight / railH);
      scroller.scrollTop = Math.min(Math.max(next, 0), maxScroll());
      update();
      show();
    });

    bar.addEventListener('pointerup', endDrag);
    bar.addEventListener('pointercancel', endDrag);

    scrollTarget.addEventListener('scroll', show, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    var observer = null;
    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(scheduleUpdate);
      observer.observe(resizeTarget);
    }

    rails.push({
      bar: bar,
      destroy: function () {
        scrollTarget.removeEventListener('scroll', show);
        window.removeEventListener('resize', scheduleUpdate);
        if (observer) observer.disconnect();
        clearTimeout(hideTimer);
        cancelAnimationFrame(frame);
      },
    });

    update();
  }

  function scan() {
    rails = rails.filter(function (rail) {
      if (rail.bar.isConnected) return true;
      rail.destroy();
      return false;
    });
    document.querySelectorAll('[data-gc-scrollbar]').forEach(createRail);
  }

  scan();
  document.addEventListener('gc:page-load', scan);
})();
